// Copyright (c) Microsoft. All rights reserved.

import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { PluginService } from '../services/PluginService';

import {
  addPlugin,
  setPluginInputValues,
  setPluginInputFormVisible,
  resetPluginInput,
} from '../../redux/features/app/pluginsSlice';
import { setCategoryModel } from '../../redux/features/studio/categorySlice';
import { PluginAPI } from '../models/app/Plugin';
import { AgentInputFormItem } from '../models/app/AgentInput';
import {
  IinputOutput,
  IPluginPromptData,
  IPluginPromptParam,
  RagContentsData,
} from '../models/studio/PluginPromptList';

export const usePlugin = () => {
  const dispatch = useAppDispatch();
  const { selectedPlugin, inputValues, isInputFormVisible } = useAppSelector((state: RootState) => state.plugins);

  const pluginService = new PluginService();

  const getAllPlugin = async (): Promise<boolean> => {
    const result = await pluginService.getAllPluginAsync().then((data) => {
      if (data.length > 0) {
        const plugins = data.map((plugin: PluginAPI) => ({
          pluginInfo: plugin,
          enabled: false,
        }));
        plugins.forEach((p) => {
          if (!p.pluginInfo.hidden) dispatch(addPlugin(p.pluginInfo));
        });
        return true;
      } else return false;
    });
    return result;
  };

  // 플러그인 카테고리 목록 조회
  const getPluginCategory = async () => {
    const result = await pluginService.getPluginCategoryAsync().then((res) => {
      const categoryList = res.data;

      const list: Array<{ value: string; label: string }> = categoryList.map((item) => ({
        value: item.id,
        label: item.categoryName,
      }));

      // const exists = list.some((item) => item.value === 'all' && item.label === '전체');

      // if (!exists) {
      //   list.unshift({ value: 'all', label: '전체' });
      // }

      dispatch(setCategoryModel(list));
      return true;
    });
    return result;
  };

  // 플러그인 리스트 조회
  const getPluginList = async (obj: IPluginPromptParam) => {
    const result = await pluginService.getPluginListAsync(obj);
    return result;
  };

  // 플러그인 즐겨찾기 조회
  const getPluginBookmark = async (obj: IPluginPromptParam) => {
    const result = await pluginService.getPluginBookmarkAsync(obj);
    return result;
  };

  //플러그인 키워드 검색
  const getPluginSearch = async (obj: IPluginPromptParam) => {
    const result = await pluginService.getPluginSearchAsync(obj);
    return result;
  };

  // 내 플러그인 조회
  const getMyPluginList = async (obj: IPluginPromptParam) => {
    const result = await pluginService.getMyPluginListAsync(obj);
    return result;
  };

  // [POST] 플러그인 생성
  const postPlugin = async (obj: IPluginPromptData) => {
    const result = await pluginService.postPluginAsync(obj);
    return result;
  };

  // [DELETE] 플러그인 삭제
  const deletePlugin = async (obj: IPluginPromptData) => {
    const result = await pluginService.deletePluginAsync(obj);
    return result;
  };

  // [GET] 플러그인 상세 조회
  const getPlugin = async (pluginId: string) => {
    const result = await pluginService.getPluginAsync(pluginId);
    return result;
  };

  // [GET] 에이전트 선택 시 상세 조회 (조회 전용)
  const getAgent = async (pluginId: string) => {
    const result = await pluginService.getAgentAsync(pluginId);
    return result;
  };

  // [PUT] 플러그인 수정
  const putPlugin = async (obj: IPluginPromptData) => {
    const result = await pluginService.putPluginAsync(obj);
    return result;
  };

  // [PUT] input/output 등록
  const putRagContent = async (obj: IinputOutput) => {
    const result = await pluginService.putRagContentAsync(obj);
    return result;
  };
  // [DELETE] input/output 삭제 - 단건
  const deleteRagContent = async (obj: IinputOutput) => {
    const result = await pluginService.deleteRagContentAsync(obj);
    return result;
  };
  // [DELETE] input/output 삭제 - 다건
  const deleteRagContents = async (obj: IinputOutput[]) => {
    const result = await pluginService.deleteRagContentsAsync(obj);
    return result;
  };

  // [POST] 플러그인 북마크(즐겨찾기) 수정
  const postPluginBookmark = async (pluginId: string) => {
    const result = await pluginService.postPluginBookmarkAsync(pluginId);
    return result;
  };

  // [DELETE] 플러그인 북마크(즐겨찾기) 삭제
  const deletePluginBookmark = async (pluginId: string) => {
    const result = await pluginService.deletePluginBookmarkAsync(pluginId);
    return result;
  };

  // [GET] 오늘 등록 갯수
  const getPluginTodayCount = async (createdStatus: number) => {
    const result = await pluginService.getPluginTodayCountAsync(createdStatus);
    return result;
  };

  // [GET] openAPI 단일 파일
  const getOpenAPI = async (openAPIId: string) => {
    const result = await pluginService.getOpenAPIAsync(openAPIId);
    return result;
  };

  // [POST] - 플러그인 파일업로드
  const postPluginRagFile = async (
    obj: { customPluginId: string; notDownloadable?: boolean; isDraft?: boolean },
    file: File,
  ) => {
    const result = await pluginService.postPluginRagFileAsync(obj, file);
    return result;
  };

  // [DELETE] 플러그인 파일 삭제
  const deletePluginRagFile = async (customPluginId: string, documentId: string) => {
    const result = await pluginService.deletePluginRagFileAsync(customPluginId, documentId);
    return result;
  };

  // [POST] 플러그인 임시저장
  const postPluginDraft = async (obj: IPluginPromptData) => {
    const result = await pluginService.postPluginDraftAsync(obj);
    return result;
  };

  // [POST] 임시저장된 상태에서 아무것도 변경되지 않고 상태만 등록으로 변경
  const postPluginStatus = async (obj: IPluginPromptData) => {
    const result = await pluginService.postPluginStatusAsync(obj);
    return result;
  };

  const getMembers = async (keyword: string) => {
    const result = await pluginService.getMembers(keyword);
    return result;
  };

  const uploadRagContentsData = async (file: File, customPluginId: string) => {
    const result = await pluginService.uploadTemplateFileAsync(file, customPluginId);
    return result;
  };

  // 데이터 템플릿 저장
  const saveRagContentsData = async (file: RagContentsData) => {
    const result = await pluginService.saveTemplateFileAsync(file);
    return result;
  };

  // 데이터 템플릿 학습
  const embeddingRagContentsData = async (file: RagContentsData) => {
    const result = await pluginService.embeddingTemplateFileAsync(file);
    return result;
  };

  // 데이터 템플릿 삭제
  const deleteRagContentsData = async (file: RagContentsData) => {
    const result = await pluginService.deleteTemplateFileAsync(file);
    return result;
  };

  // [GET] aionu 에이전트 목록 조회
  const getAionu = async () => {
    const result = await pluginService.getAionuAsync();
    return result;
  };

  // [GET] 에이전트 자동완성 (검색어 기반)
  const getAgentAutocomplete = async (query?: string, count?: number) => {
    const limit = typeof count === 'number' ? count : 10;
    const result = await pluginService.getAgentAutocompleteAsync(query, limit);
    return result;
  };

  // ---------------------------------------------------
  // 변수 입력 폼
  // ---------------------------------------------------
  const agentInputForm: AgentInputFormItem[] = selectedPlugin?.aiOnUIConnectData?.userInputForm ?? [];

  const setInputFormVisible = useCallback((visible: boolean) => {
    dispatch(setPluginInputFormVisible(visible));
  }, []);

  const submitInputValues = useCallback((values: Record<string, string>) => {
    dispatch(setPluginInputValues(values));
    dispatch(setPluginInputFormVisible(false));
  }, []);

  const resetInput = useCallback(() => dispatch(resetPluginInput(undefined)), []);

  return {
    getAllPlugin,
    getPluginCategory,
    getPluginList,
    getPluginBookmark,
    getPluginSearch,
    getMyPluginList,
    postPlugin,
    deletePlugin,
    getPlugin,
    getAgent,
    putPlugin,
    putRagContent,
    deleteRagContent,
    deleteRagContents,
    postPluginBookmark,
    deletePluginBookmark,
    getPluginTodayCount,
    postPluginRagFile,
    deletePluginRagFile,
    postPluginDraft,
    postPluginStatus,
    getMembers,
    uploadRagContentsData,
    saveRagContentsData,
    embeddingRagContentsData,
    deleteRagContentsData,
    getOpenAPI,
    getAionu,
    getAgentAutocomplete,

    // 변수 입력 폼
    agentInputForm,
    inputValues,
    isInputFormVisible,
    setInputFormVisible,
    submitInputValues,
    resetInput,
  };
};
